
import {
  ref,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  defineComponent,
  useContext,
  useRouter,
  useAsync,
  watch
} from '@nuxtjs/composition-api'

// Components
import {
  Warning,
  Preloader,
  Notification,
  AppButton,
  AnimatedButton
} from '@shared/ui'
import Bugsnag from '@bugsnag/js'

import { PayLockModal, usePayModel } from '@entities/pay'

import { Footer } from '@widgets/footer'
import { privacyPolicy } from '@widgets/privacy-policy'

import { RestaurantPreloader } from '@entities/restaurant-preloader'
import { Header } from '@widgets/header'
import { Menu, MenuCart } from '@widgets/menu'
import {
  useRestaurantModel,
  RestaurantNotWorkingModal,
  RestaurantNotWorking
} from '@entities/restaurant'
import { DiffCheck } from '@widgets/diff-check'
import { useDiffCheckModel } from '@features/diff-check'
import { SharedButton } from '@features/shared/ui'
import { QuizButton } from '@widgets/quiz/ui/quiz-button'

import { IframeModal } from '@entities/iframe-modal'
import { Review } from '@widgets/review'
// import { ServiceFee } from '@features/service-fee'
import { PayedVariants } from '@widgets/payed-variants'
import { CallWaiter } from '@widgets/call-waiter'
import { WiFiModal, useWiFiModel } from '@entities/wifi'
import { EmptyOrder } from '@entities/empty-order/ui/empty-order'
import { sendStatistic } from '@entities/qr-type'
import { TipsBlock, roundFee, useTipsModel, isWaiterVerified, areTipsActive } from '@entities/tips'
import { discountGetData } from '@entities/discount'
// import { useRedirectModel } from '@entities/safe-link'
import { ReviewStars, ReviewComments, useReviewModel } from '@entities/review'

import {
  pay as orderPay,
  calcTotalSum,
  calcSumSelectedElements,
  checkChangeOrder,
  parsUrlForPay,
  redirectEmptyOrderForTips,
  useOrderModel,
  countNotPayedElements,
  fetch as orderFetch,
  saveSbpWait,
  readSbpWait,
  clearSbpWait
} from '@entities/order'

import {
  getSelectedID,
  getStatusPayed
} from '@entities/order-item'
import { parseUrl, togglePageScroll } from '@shared/utils'
import { checkVpn } from '@entities/vpn'

import { getRestCodeOnLoadPage, getRestCodePayload, checkLinkTips, applyAdvanceCheckType } from '@entities/restaurant/utils'
import { retryOnNetworkError } from '@shared/api'
import { isMenuQrTable, shouldHideFooterForMenuQrTable } from '@entities/restaurant/utils/menuTableRules'

import { useBanksModel } from '@entities/banks'
import { useWarningModel, useCustomLogicModel } from '@shared/model'
import { useOperationsModel } from '@entities/operations'
import { useTogglerModel } from '@shared/model/toggler'
import { useThemeModel } from '@entities/theme'
import { usePreloaderModel } from '@shared/model/preloader'
import { useNotificationModel } from '@shared/model/notification'
import { useOrderModelWidget, OrderTotal, OrderList } from '@widgets/order'
import { usePrivacyPolicyModel } from '@features/privacy-policy'
import { useMenuModel } from '@entities/menu'
import { useStocksModel } from '@entities/stocks'
import { useBrowserModel } from '@entities/browser'
import { useCallWaiterModel } from '@entities/call-waiter'
import { useMetaModel } from '@entities/meta'
import {
  amplitudeInitialize,
  amplitudeLogEvent,
  amplitudeEvents,
  Cookie
} from '@shared/lib'

import { SERVICE_FEE_PERCENTS, SERVICE_FEE_PERCENTS_CHOCOLATE, SERVICE_FEE_PERCENTS_PIVALDI, SERVICE_FEE_MAX, SERVICE_FEE_MAX_OLD } from '@/constants'
import { useLoyaltyModel } from '~/src/entities/loyalty'
// import AbpayModal from '~/src/widgets/abpay-modal/AbpayModal.vue'

export default defineComponent({
  name: 'RestaurantPage',
  components: {
    OrderTotal,
    Header,
    SharedButton,
    Footer,
    QuizButton,
    privacyPolicy,
    Menu,
    MenuCart,
    RestaurantPreloader,
    RestaurantNotWorkingModal,
    RestaurantNotWorking,
    Warning,
    PayedVariants,
    OrderList,
    // ServiceFee,
    WiFiModal,
    EmptyOrder,
    Preloader,
    Notification,
    IframeModal,
    PayLockModal,
    Review,
    DiffCheck,
    CallWaiter,
    TipsBlock,
    ReviewStars,
    ReviewComments,
    AppButton,
    AnimatedButton
    // AbpayModal
  },

  setup () {
    const router = useRouter()

    // <<<---------- Store ---------->>>
    const bankModel = useBanksModel()
    const restaurantModel = useRestaurantModel()
    const operationsModel = useOperationsModel()
    const warningModel = useWarningModel()
    const togglerModel = useTogglerModel()
    const wifiModel = useWiFiModel()
    const themeModel = useThemeModel()
    const preloaderModel = usePreloaderModel()
    const notificationModel = useNotificationModel()
    const orderModelWidget = useOrderModelWidget()
    const orderModel = useOrderModel()
    const customLogicModel = useCustomLogicModel()
    const privacyPolicyModel = usePrivacyPolicyModel()
    const menuModel = useMenuModel()
    const stocksModel = useStocksModel()
    const payModel = usePayModel()
    const diffCheckModel = useDiffCheckModel()
    const browserModel = useBrowserModel()
    const callWaiterModel = useCallWaiterModel()
    const tipsModel = useTipsModel()
    const metaModel = useMetaModel()
    const loyaltyModel = useLoyaltyModel()
    const reviewModel = useReviewModel()
    // const redirectModel = useRedirectModel()

    // <<<---------- Plugins ---------->>>
    const {
      $tapperBrowserPrint,
      $fingerprint,
      $cookies,
      $yandexMetrika,
      req,
      $echo,
      redirect,
      route
    } = useContext()

    // <<<---------- AsyncData ---------->>>
    const META_REQUEST_TIMEOUT_MS = 3000
    const asyncData = useAsync(async () => {
      // console.log('route.value.query.qr_type', route.value.query.qr_type)
      // if (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2') {
      //   // Безопасная ссылка
      //   const tableId = route.value.params.id
      //   const shopCode = route.value.params.restaurant
      //   const redirectSafeLink = await redirectModel.getRedirectUrl(tableId, shopCode)
      //   if (`${route.value.path}?field=${route.value.query.field}` !== redirectSafeLink.url) {
      //     const redirected = await parseUrlSafeLink(redirectSafeLink.url, req, redirect)
      //     if (redirected) { return } // Прерываем выполнение
      //   }
      // }
      // Парсинг url и преобразование его в корректный
      parseUrl(route.value.fullPath, req, redirect)
      // GetRestName
      await getRestCodeOnLoadPage(route.value)
      // Формирование ссылки дял Tips
      checkLinkTips(route.value, redirect)

      // Проверка ошибок в операции
      operationsModel.checkError()

      // Мета теги нужны только для превью ссылки. Браузер гостя шлёт Sec-Fetch-*, превьюеры мессенджеров — нет,
      // поэтому гостю HTML с анимацией загрузки отдаётся без ожидания бэкенда
      const isBrowserNavigation = req?.headers['sec-fetch-mode'] === 'navigate' &&
        req?.headers['sec-fetch-dest'] === 'document'
      // Без кода ресторана (не дождались determine-code) название не получить
      if (isBrowserNavigation || !restaurantModel.code) {
        return {}
      }

      // Медленный бэкенд не держит отдачу страницы.
      // По таймауту проверки на 404 нет — несуществующий ресторан отсеется на клиенте
      try {
        const descMeta = await restaurantModel.getMetaDesription(META_REQUEST_TIMEOUT_MS)
        return {
          descMeta
        }
      } catch (e) {
        if (e.response?.status === 404) {
          redirect('/404')
        }
      }
    })

    // <<<---------- Data ---------->>>
    const pageLoaded = ref(false) // Страница загрузилась
    const orderLoadError = ref(false) // Заказ не загрузился при открытии страницы
    const orderLoadRetrying = ref(false)
    let initReachedOrder = false // Подготовка страницы дошла до загрузки заказа
    // Лимиты только для запросов, без которых не открывается экран заказа.
    // У заказа запас под медленную кассу
    const SCREEN_REQUEST_TIMEOUT_MS = 10000
    const ORDER_LOAD_TIMEOUT_MS = 25000
    const FINGERPRINT_TIMEOUT_MS = 3000
    // Сброс отметок позиций при загрузке идёт в фоне (перед оплатой он повторяется);
    // лимит — чтобы поздний ответ не перетёр то, что гость успел сделать с позициями
    const POSITIONS_RESET_TIMEOUT_MS = 5000
    // Отпечаток считается в фоне: order/get его не использует, ждут только запросы, которым он нужен
    let visitorDataPromise = null
    // Чтобы анимация загрузки не мелькала, если всё загрузилось мгновенно
    const PRELOADER_MIN_MS = 3000
    const discount = ref(0) // Скидка
    const recipient = ref('')// Получатель сообщений вызова официанта
    const responseTemplate = ref('')// Шаблон сообщения вызова официанта
    const serviceFeeActive = ref(true) // Сервисный сбор
    const successfulScan = ref(false) // попап удачного применения скидки после сканирования
    const currencyTypeSelected = ref(null) // Выбор валюты
    const modalPolicy = ref(null) // Показать модалку политики конфиденциальности
    const tipsType = ref('')// Тип чаевых
    const statusPayment = ref(null) // Статус оплаты
    const messageErrorPayment = ref('')// Сообщение при ошибке оплаты
    const paymentMethod = ref('sbp') // Тип оплаты
    const paymentMethodModal = ref(false) // Модалка выбора типа оплаты
    const sbpModal = ref(false) // Модалка с основными банками
    const modalPayAlert = ref(false) // Предупреждение прерывания оплаты
    const banksModal = ref('priority') // Модалка выбора банка
    const sbpPayFlag = ref(false) // временное решение когда отваливаются сокеты после оплаты СБП
    const paymentWait = ref(false) // ожидание оплаты
    const applyDiscount = ref(false) // модалка применения скидки
    const waitingDiscount = ref(false) // модалка ожидания применения скидки после сканирования
    const failureApplyDiscount = ref(false) // попап неудачи применения скидки после сканирования
    const scanLoading = ref(false) // попап ожидания скидки после сканирования
    const discountProcess = ref(false) // попап 'Скидка уже применяется'
    const disabledWaitingDiscount = ref(false)
    const scanner = ref(false)
    const paymentWithoutDiscount = ref(false)
    const menuFooter = ref('') // футер меню
    const hasDiscount = ref(false) // алерт 'У вас уже есть скидка'
    const promocodeError = ref(false) // алерт 'Не удалось применить скидку.'
    const waitingPromocode = ref(false) // алерт 'Ожидаем применение скидки'
    const acceptingPromocode = ref(false) // применен ли промокод
    const waitingAcceptPromocode = ref(false) // ожидаем применения промокода
    const activeModalBonus = ref('')
    const activePopUpGreeting = ref(false) // активность модалки приветствия
    const verticalScroll = reactive({ // Положение вертикального скролла по разделам
      menu: 0,
      cart: 0,
      order: 0
    })
    const choosenBank = ref(null) // банк, выбранный для оплаты (нужен для paymentWithoutDiscountModal)
    const stopCheckPayment = ref(false)
    const modal = ref(null) // активность разных модалок
    const b2pError = ref(false)
    const qrType = ref('standart')
    const noFooter = ref(false)
    const precheck = ref(false)
    const rating = ref(0) // рейтинг (отзывы)
    const previousRating = ref(0) // предыдущее значение рейтинга для анимации
    const comment = ref('') // комментарий отзыва
    const disabledReviewBtn = ref(false) // disabled для кнопки отправки отзыва
    const reviewSubmitted = ref(false) // флаг успешной отправки отзыва

    // <<<---------- Watch ---------->>>
    watch(menuFooter, (newVal) => {
      if (newVal === 'order') {
        window.scroll(0, verticalScroll.order) // Скроллим страницу на прежнее положение
      }
    })

    watch(() => tipsModel.tipsSum, () => {
      if (tipsModel.tipsSum === 0) {
        setPaymentMethod('sbp')
      }
    })

    // <<<---------- Computed ---------->>>
    const hideFooterForMenuQr = computed(() => {
      const tableId = restaurantModel.tableCode || route.value.params.id
      return shouldHideFooterForMenuQrTable(restaurantModel.code, tableId)
    })

    // fingerprintId
    const fingerprintId = computed(() => {
      const [fingerprintId] = (browserModel.browserId || []).filter(i => i.plugin === 'fingerprint')
      return fingerprintId
    })

    // Проверяем, что выделены все элементы другим пользователем
    const selectedElements = computed(() => {
      return orderModel.orderElements.every(element => element.selected_guest_id !== orderModel.orderData.guest && element.selected_guest_id !== null)
    })

    // Ешз чаевых
    const computedTipsType = computed(() => tipsType.value)

    const calcNotPayedElements = computed(() => {
      return orderModel.orderElements.reduce((acc, el) => {
        return el.status === 'NOT_PAID' ? acc + 1 : acc
      }, 0)
    })

    // Выбор валюты
    const currencyIcon = computed(() => {
      if (currencyTypeSelected.value?.designation === 'KGS') {
        return `<span class="som-font">${currencyTypeSelected.value?.unicode}</span>`
      }
      return (
        currencyTypeSelected.value?.icon ||
          currencyTypeSelected.value?.unicode ||
          '₽'
      )
    })

    // Валюта для Cloud
    const currencyType = computed(() => {
      return currencyTypeSelected.value?.designation || 'RUB'
    })

    const specialServiceFeeRestaurants = [
      'starik-hinkalich-novomoskovsk-14560',
      'est-hinkali-pit-vino-povarskaya-9904',
      'perchini72-6892',
      'starik-hinkalich-rostov-3-14567',
      'fukuramen-7056',
      'bashi-gruzinskoe-bistro-4826',
      'imperial-16003',
      'MOS-lounge-bar-17805',
      'deribas-435',
      'dzen-25612',
      'myaso-i-ugli-2346',
      'imperial-8232',
      'garagh-27998',
      'sedlo-24753',
      'vaha-lavka-gelendghik-5567',
      'lyubimaya-ya-u-mami-19960',
      'grustnii-bar-8696',
      'evraziya-9787'
    ]

    // Рассчитываем сервисный сбор
    const serviceFeeCalculate = computed(() => {
      if (orderModel.orderData && orderModel.orderData.prepayment > orderModelWidget.sumOrder) {
        return 0
      }
      if (totalSumWithPrepayment.value > 0) {
        const waiter = orderModel.orderData.waiter
        const hasWaiter = waiter !== null && !(Array.isArray(waiter) && waiter.length === 0)
        const tipsFeeEligible =
          tipsModel.tips.hookah.active ||
          tipsModel.tips.kitchener.active ||
          tipsModel.tips.sommelier.active ||
          tipsModel.tips.barman.active ||
          tipsModel.tips.barista.active ||
          tipsModel.tips.team.active ||
          (hasWaiter && waiter.card_verification)
        const checkTips = tipsFeeEligible ? (tipsModel.tipsSum / 100) * 5 : 0
        let result = parseFloat(((totalSumWithPrepayment.value / 100) * serviceFeePercent.value + checkTips))
        const isSpecialRest = specialServiceFeeRestaurants.includes(restaurantModel.code)
        let isMinFeeApplied = false

        if (isSpecialRest && Number(tipsModel.tipsSum) === 0) {
          if (result < 39) {
            result = 39
            isMinFeeApplied = true
          }
        }

        if (customLogicModel.list.oldFeeCalc) {
          if (isMinFeeApplied) {
            return result
          }
          if (customLogicModel.list.oldFeeCalcRoundDown) {
            return Math.floor(result) > SERVICE_FEE_MAX_OLD ? SERVICE_FEE_MAX_OLD : Math.floor(result)
          } else {
            return Math.ceil(result) > SERVICE_FEE_MAX_OLD ? SERVICE_FEE_MAX_OLD : Math.ceil(result)
          }
        }
        if (isMinFeeApplied) {
          return result
        }
        return roundFee(result) > SERVICE_FEE_MAX ? SERVICE_FEE_MAX : roundFee(result)
      } else {
        return 0
      }
    })

    // Проверяем, все ли товары оплачены
    const showOrderList = computed(() => {
      if (orderModel.orderElements && orderModel.orderElements.length) {
        const res = {
          status: orderModel.orderElements.every(
            product => product.status === 'PAID'
          ),
          active: !orderModel.orderElements.every(
            product => product.status === 'PAID'
          )
        }
        return res
      }
      return { status: false, active: false }
    })

    // Проверка на раздельный счет другим гостем
    const separateOrderAlienGuest = computed(() => {
      return orderModel.orderElements.some(
        element =>
          element.selected_guest_id &&
            element.selected_guest_id !== metaModel.guest &&
            element.status === 'NOT_PAID'
      )
    })

    // Сумма выбранных позиций
    const selectedSum = computed(() => {
      if (!orderModel.divideCheck) {
        // добавили логику обработки 1 позиции и выкл разд счет
        if (orderModel.orderData && orderModel.orderData.orders) {
          const selectedSumOtherUser = orderModel.orderElements.reduce((sum, element) => {
            if (element.selected_guest_id && element.status === 'NOT_PAID') {
              return sum + element.price / 100
            }
            return sum + 0
          }, 0)
          if (orderModel.orderData.bonus > 0) {
            return ((orderModel.orderData.orders?.reduce((sum, { fullPaySum }) => sum + fullPaySum, 0) / 100) + discount.value - orderModel.orderData.prepayment - (orderModel.orderData.bonus / 100)) - selectedSumOtherUser ?? 0
          } else {
            return ((orderModel.orderData.orders?.reduce((sum, { fullPaySum }) => sum + fullPaySum, 0) / 100) + discount.value) - selectedSumOtherUser - (orderModel.orderData.prepayment ?? 0)
          }
        }
      }
      const calcSumSelectedElements1 = parseFloat(calcSumSelectedElements(
        orderModel.orderElements,
        metaModel.guest
      ).toFixed(2))
      return calcSumSelectedElements1
    })

    // Сумма выбранных позиций c депозитами
    const selectedSumWithPrepeyment = computed(() => {
      // добавили логику обработки 1 позиции и выкл разд счет
      if (!orderModel.divideCheck) {
        if (orderModel.orderData && orderModel.orderData.orders) {
          const selectedSumOtherUser = orderModel.orderElements.reduce((sum, element) => {
            if (element.selected_guest_id && element.status === 'NOT_PAID') {
              return sum + element.price / 100
            }
            return sum + 0
          }, 0)
          if (orderModel.orderData.bonus > 0) {
            return ((orderModel.orderData.orders?.reduce((sum, { fullPaySum }) => sum + fullPaySum, 0) / 100) + discount.value - orderModel.orderData.prepayment - (orderModel.orderData.bonus / 100)) - selectedSumOtherUser ?? 0
          } else {
            return ((orderModel.orderData.orders?.reduce((sum, { fullPaySum }) => sum + fullPaySum, 0) / 100) + discount.value) - selectedSumOtherUser - (orderModel.orderData.prepayment ?? 0)
          }
        }
      }
      const a = parseFloat(calcSumSelectedElements(
        orderModel.orderElements,
        metaModel.guest,
        true
      ).toFixed(2))
      return a
    })

    // Сумма к оплате по выбранным позициям \ скидками
    const totalSum = computed(() => {
      // TODO: Для задачи с округлением
      if (!orderModel.divideCheck) {
        if (orderModel.orderData && orderModel.orderData.orders) {
          const selectedSumOtherUser = orderModel.orderElements.reduce((sum, element) => {
            if (element.selected_guest_id && element.status === 'NOT_PAID') {
              return sum + element.price / 100
            }
            return sum + 0
          }, 0)
          if (orderModel.orderData.bonus > 0) {
            return ((orderModel.orderData.orders?.reduce((sum, { fullPaySum }) => sum + fullPaySum, 0) / 100) + discount.value - orderModel.orderData.prepayment - (orderModel.orderData.bonus / 100)) - selectedSumOtherUser ?? 0
          } else {
            return ((orderModel.orderData.orders?.reduce((sum, { fullPaySum }) => sum + fullPaySum, 0) / 100) + discount.value) - selectedSumOtherUser - (orderModel.orderData.prepayment ?? 0)
          }
        }
        return 0
      }
      return calcTotalSum(
        orderModel.orderElements,
        diffCheckModel.status,
        selectedSum.value,
        false
      )
    })

    // Сумма к оплате по выбранным позициям c учетом депозита
    const totalSumWithPrepayment = computed(() => {
      return calcTotalSum(
        orderModel.orderElements,
        diffCheckModel.status,
        selectedSumWithPrepeyment.value,
        true
      )
    })

    // Итоговая сумма к оплате, со всеми сборами т.п.
    const totalSumToPay = computed(() => {
      const checkTotalSum = totalSum.value > 0 ? totalSum.value : 0
      return serviceFeeActive.value
        ? (
            parseFloat(checkTotalSum) + parseFloat(serviceFeeCalculate.value)
          ).toString()
        : checkTotalSum.toString()
    })

    // Показывать или не показывать в блоке суммах чаевые
    const showBlockTips = computed(() => {
      const waiter = orderModel.orderData.waiter
      const team = orderModel.orderData.team

      // если waiter null или пустой массив, сразу false
      if (team === null || (Array.isArray(team) && team.length === 0)) {
        if (waiter === null || (Array.isArray(waiter) && waiter.length === 0)) {
          return false
        }
      }

      let res = false
      for (const i in orderModel.orderData.tips) {
        if (orderModel.orderData.tips[i].active) {
          res = true
        }
      }

      return res ||
        (
          tipsType.value === 'TAPPER' &&
          waiter !== undefined &&
          waiter !== null &&
          waiter.name !== null &&
          waiter.waiter_status === 'VERIFIED'
        ) ||
        (
          tipsType.value === 'TAPPER' &&
          team !== undefined &&
          team !== null
        )
    })

    // Показывать чаевые или нет
    const showTips = computed(() => {
      return (
        Object.values(tipsModel.tips).some(tip => tip.active) ||
          (tipsType.value === 'TAPPER' && isWaiterVerified(orderModel.orderData.waiter)) ||
          (tipsType.value === 'TAPPER' && orderModel.orderData.tips.length > 0 && areTipsActive(orderModel.orderData.tips) && isWaiterVerified(orderModel.orderData.waiter))
      )
    })

    const serviceFeePercent = computed(() => {
      if (restaurantModel.code === 'shokoladnica-citymall') {
        return SERVICE_FEE_PERCENTS_CHOCOLATE
      }
      if (restaurantModel.code === 'pivaldi' || restaurantModel.code === 'pivaldi-odincovo-925' || restaurantModel.code === 'pivaldi-zelenograd-2865' || restaurantModel.code === 'pivaldi-mitishi-14751' || restaurantModel.code === 'pivaldi-city-29578') {
        return SERVICE_FEE_PERCENTS_PIVALDI
      }
      if (customLogicModel.list.feeThreeHalfPercent) {
        return 3.5
      }
      return SERVICE_FEE_PERCENTS
    })

    // <<<---------- Methods ---------->>>

    // Клик по звездочкам
    const handleRating = (val) => {
      const reviewStars = document.querySelector('.review-stars')
      if (!reviewStars) { return }

      // Определяем направление изменения рейтинга
      const isIncreasing = val > previousRating.value
      const isDecreasing = val < previousRating.value

      // Убираем старые классы
      reviewStars.classList.remove('rating-increase', 'rating-decrease')

      // Добавляем класс в зависимости от направления
      if (isIncreasing) {
        reviewStars.classList.add('rating-increase')
      } else if (isDecreasing) {
        reviewStars.classList.add('rating-decrease')
      }

      // Анимируем звезды последовательно
      const stars = reviewStars.querySelectorAll('li')
      const start = Math.min(previousRating.value, val)
      const end = Math.max(previousRating.value, val)

      stars.forEach((star, index) => {
        const starNumber = index + 1
        // Убираем предыдущие классы анимации
        star.classList.remove('star-animate-in', 'star-animate-out')
        // Анимируем только звезды в диапазоне изменения
        if (starNumber > start && starNumber <= end) {
          const delay = (starNumber - start - 1) * 0.08
          star.style.animationDelay = `${delay}s`
          if (isIncreasing) {
            star.classList.add('star-animate-in')
          } else if (isDecreasing) {
            star.classList.add('star-animate-out')
          }
        }
      })

      // Обновляем значения
      previousRating.value = rating.value
      rating.value = val
    }

    // Изменение комментария
    const onChangeComment = (val) => {
      comment.value = val
    }

    // Отправка отзыва
    const sendReview = async () => {
      if (disabledReviewBtn.value) {
        return
      }

      // Проверяем, что есть рейтинг или комментарий
      if (rating.value === 0 && !comment.value.length) {
        return
      }

      disabledReviewBtn.value = true

      try {
        const { sendMessage } = await import('@features/telegram')
        const { generateWaiterName, generateMessage } = await import('@widgets/review/utils')

        // Генерация имени официанта
        const waiter = orderModel.orderData?.waiter || null
        const waiterName = generateWaiterName(waiter)

        // Текст сообщения (упрощенная версия без пожеланий)
        const textOfMessage = generateMessage(
          orderModel.transactionData,
          restaurantModel.tableCode,
          waiterName,
          comment.value,
          rating.value,
          '' // без пожеланий в упрощенной версии
        )

        const userId = orderModel.orderData?.waiter?.id || null
        const transactionLastId = orderModel.transactionData?.session_ids?.paid_transaction_ids.at(-1)

        await sendMessage({
          shopCode: restaurantModel.code,
          message: textOfMessage,
          messageType: 'RATING',
          transactionId: transactionLastId,
          orderId: null,
          userId
        })

        // Показываем блок благодарности
        reviewSubmitted.value = true

        // Анимация успеха
        const reviewBlock = document.querySelector('.review__common')
        if (reviewBlock) {
          reviewBlock.classList.add('review-success')
          setTimeout(() => {
            reviewBlock.classList.remove('review-success')
          }, 600)
        }

        // Сбрасываем форму
        rating.value = 0
        previousRating.value = 0
        comment.value = ''
      } catch (error) {
        notificationModel.toggleNotification({
          status: true,
          text: 'Не удалось отправить отзыв. Попробуйте позже.',
          variant: 'danger'
        })
      } finally {
        disabledReviewBtn.value = false
      }
    }

    // Восстановление раздельного счета
    const restoreDiffCheck = async () => {
      const notPayedElements = orderModel.orderElements.filter(i => i.status === 'NOT_PAID')
      const selectedElementsCheck = orderModel.orderElements.some(i => i.selected)

      if (notPayedElements.length === 1) {
        await orderModel.selectOrderPosition({
          id: notPayedElements[0].id,
          status: true,
          guest: metaModel.guest
        })
      }

      if (
        countNotPayedElements(orderModel.orderElements) > 1 &&
        notPayedElements.length > 1 &&
        orderModel.orderData.discount === 0 &&
        selectedElementsCheck
      ) {
        diffCheckModel.status = $cookies.get('diffCheck')
      } else {
        diffCheckModel.status = false
      }
    }

    // Проверка данных заказа и подгрузка их вновь
    const checkOrderData = async () => {
      preloaderModel.status = true
      await restaurantModel.getStatus({
        restaurant: restaurantModel.code,
        // tableId: (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
        //   ? route.value.query.field
        //   : route.value.params.id
        tableId: route.value.params.id
      })
      if (restaurantModel.stubTypes.includes('FULL')) {
        setTimeout(() => {
          preloaderModel.status = false
          restaurantModel.showModal = true
        }, 1000)
      } else {
        await getOrderData(true)
        preloaderModel.status = false
      }
    }

    // Напоминание PrimeHill о списании бонусов
    const primeHillReminder = () => {
      const primeHllReminder = Cookie.get('primeHillReminder')
      if (orderModel.orderData?.global_system_loyalty?.code === 'prime_hill' && primeHllReminder && orderModel.orderData?.session) {
        const dateNow = Number(Date.now())
        const timeDifference = dateNow - Number(primeHllReminder)
        const hoursDifference = timeDifference / (1000 * 60 * 60)
        if (hoursDifference > 5) {
          notificationModel.toggleNotification({
            status: true,
            text: `${loyaltyModel.userNameGreeting}, для управления бонусами перейдите в &#171;Ваши бонусы&#187;`,
            variant: 'primeHill',
            time: 5000
          })
        }
      }
    }

    // Получение данных заказа
    const getOrderData = async (showModal) => {
      // Получаем данные из куки
      const session = await $cookies.get('session')

      // ID гостя
      let guestId = await sessionStorage.getItem('guest')
      guestId = guestId ? parseInt(guestId) : parseInt(Date.now())
      metaModel.guest = Number(guestId)
      sessionStorage.setItem('guest', guestId)

      // Получаем список заказанных блюд

      await orderModel.fetchOrder({
        // tableId: (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
        //   ? route.value.query.field
        //   : route.value.params.id,
        tableId: route.value.params.id,
        domen: restaurantModel.code,
        session: null,
        guest: guestId,
        browserId: fingerprintId.value,
        orderGuid: route.value.query.order_guid,
        timeout: ORDER_LOAD_TIMEOUT_MS
      })
      // Если активность у реста отключена
      if (!orderModel.orderData?.active) {
        restaurantModel.stubTypes.includes('FULL')
        if (showModal) {
          restaurantModel.showModal = true
        }
        return
      }

      // Сохраняем ID браузера
      let yandexClientID = ''
      $yandexMetrika.getClientID((clientID) => {
        yandexClientID = clientID
      })
      if (orderModel.orderData.session) {
        const shopId = parseInt(orderModel.orderData.settings.shop_id)
        const sessionId = parseInt(orderModel.orderData.session)
        Promise.resolve(visitorDataPromise).then(() => browserModel.saveIdBrowser({
          id: guestId,
          shopId,
          browserId: browserModel.browserId,
          sessionId,
          clientID: yandexClientID
        })).catch((e) => {
          // eslint-disable-next-line no-console
          console.log('saveIdBrowser', e)
        })
      }

      // Получаем сессию
      metaModel.session = orderModel.orderData.session
      !session &&
        $cookies.set('session', String(orderModel.orderData.session))

      // Запускаем сокеты
      runSockets()

      // Загружаем данные для проекта
      tipsType.value = orderModel.orderData.tipsType
      setDiscount()

      recipient.value = orderModel.orderData.recipient
      responseTemplate.value = orderModel.orderData.responseTemplate
      currencyTypeSelected.value = orderModel.orderData.currencyTypeSelected

      // Отправка статистики пречека
      await sendStatistic(metaModel.session, route.value.query.qr_type)

      // Очищаем query параметры из роутов
      qrType.value = route.value.query.qr_type ?? 'standart'
      // const cocationArr = route.value.fullPath.split('?')
      // window.history.pushState('name', '', cocationArr[0])
      const platform = route.value.query.platform
      const orderGuid = route.value.query.order_guid
      const dopID = route.value.query.i
      const tableId = route.value.query.table_ID
      // router.push({ query: null })

      if (orderModel.orderData.settings) {
        wifiModel.fetchData(orderModel.orderData.settings.shop_id).catch((e) => {
          // eslint-disable-next-line no-console
          console.log('Wi-Fi loading failed', e)
        })
        await orderModel.fetchDevideCheck(orderModel.orderData.settings.shop_id)
      }
      // Проверка есть ли отмеченные позиции пользователем
      const checkSelectedPosition = orderModel.orderElements.some(
        i => i.selected
      )
      if (
        (diffCheckModel.status) || (checkSelectedPosition && orderModel.divideCheck)
      ) {
        toggleDiffCheck()
      }

      // Проверка на пречек
      checkPrecheck()

      // Проверка на раздельный счет
      if (!orderModel.divideCheck) {
        const elements = orderModel.orderElements.filter(i => i.selected || i.selected_guest_id === metaModel.guest).map(i => i.id)
        if (elements.length) {
          orderModel.selectOrderPosition({
            id: elements,
            status: true,
            guest: metaModel.guest,
            transactionReset: true,
            timeout: POSITIONS_RESET_TIMEOUT_MS
          })
        }
      }

      // Проверяем включен ли сервисный сбор в админке
      if (orderModel.orderData.fee_type === 'WITHOUT_FEE') { serviceFeeActive.value = false }

      // <--- Novikov Start --->
      // Проверяем скидку по Новикову --- {
      // if (orderModel.orderElements.length === 0) {
      //   this.$cookies.set('popapDiscount', null)
      // }

      const novikovUrl = route.value.fullPath.includes('novikov')
      const popapDiscount = $cookies.get(`popapDiscount_${orderModel.orderData.session}`)
      if (novikovUrl && orderModel.orderData.discount !== 0 && !popapDiscount) {
        $cookies.set(`popapDiscount_${orderModel.orderData.session}`, true, {
          maxAge: 60 * 60 * 24 * 1
        })
        successfulScan.value = true
        setTimeout(() => {
          successfulScan.value = false
        }, 5000)
      }

      const cocationArrNovikov = route.value.fullPath.split('?')
      window.history.pushState('name', '', cocationArrNovikov[0])
      router.push({ query: null })
      // --- }
      // <--- Novikov End --->

      // ----- Проверяем скидку по промокоду ----------------
      if (customLogicModel.list.promocode) {
        const savingOrderId = await $cookies.get('order_id')

        if (orderModel.orderData?.orders.length && savingOrderId === orderModel.orderData?.orders[0]?.order_id) {
          if (orderModel.orderData.discountStatus === 'WAITING') {
            waitingAcceptPromocode.value = true
          }

          if (orderModel.orderData.discountStatus === 'APPLIED') {
            acceptingPromocode.value = true
          }
        }
      }
      // ----------------------------------------------------

      // --------- Получаем юзера PrimeHill ----------------
      await fetchPrimeHillUser()
      // ----------------------------------------------------
      // Возвращаем тип платформы в query параметры
      const queryParams = {
        qr_type: qrType.value,
        platform,
        order_guid: orderGuid
      }
      if (dopID) {
        queryParams.i = dopID
      }

      if (tableId) {
        queryParams.table_ID = tableId
      }
      if (qrType.value !== 'standart') {
        router.push({
          query: queryParams
        })
      }

      if (!restaurantModel.tableCode) {
        if (!restaurantModel.tableCode && tableId) {
          restaurantModel.tableCode = tableId
        } else {
          restaurantModel.tableCode = orderModel.orderData.tableCode
        }
      }

      // Метрики ID 338655630
      if (separateOrderAlienGuest.value) {
        callYandexMetricGoal('separateOrderAlienGuest')
      }
    }

    const reportOrderLoadError = (e) => {
      // eslint-disable-next-line no-console
      console.log('orderLoad', e)
      if (Bugsnag._client) {
        Bugsnag.notify(e)
      }
    }

    // Повторная загрузка заказа после ошибки при открытии страницы
    const retryOrderLoad = async () => {
      if (orderLoadRetrying.value) {
        return
      }
      // Без статуса ресторана getOrderData не отработает
      if (!initReachedOrder) {
        window.location.reload()
        return
      }
      orderLoadRetrying.value = true
      try {
        await getOrderData()
        orderLoadError.value = false
      } catch (e) {
        reportOrderLoadError(e)
        orderLoadError.value = !orderModel.orderData
        if (orderLoadError.value) {
          notificationModel.toggleNotification({
            status: true,
            text: 'Не удалось загрузить заказ. Попробуйте ещё раз',
            variant: 'danger'
          })
        }
      } finally {
        if (!menuFooter.value) {
          menuFooter.value = 'order'
        }
        orderLoadRetrying.value = false
      }
    }

    // вызов официанта
    const callWaiter = () => {
      callWaiterModel.toggleModal()
      togglePageScroll(callWaiterModel.showModal)
    }

    // переключение элементов в футере
    const menuLinks = {
      'china-club-9122': {
        url: 'https://www.novikovgroup.ru/restaurants/detail/china-club/',
        openInNewTab: false
      },
      'sirovarnya-17985': {
        url: 'https://www.syrovarnya.com/catalog/vnukovo?section=popular',
        openInNewTab: true
      },
      'sirovarnya-zelenograd-24684': {
        url: 'https://www.syrovarnya.com/catalog/zelenograd?section=popular',
        openInNewTab: true
      }
    }

    const toggleMenuFooter = (val) => {
      if (val === 'menu' && restaurantModel.code in menuLinks) {
        const { url, openInNewTab } = menuLinks[restaurantModel.code]
        if (openInNewTab) {
          window.open(url, '_blank')
        } else {
          window.location.href = url
        }
        callYandexMetricGoal('clickMenu')
        return
      }

      if (val === 'menu') {
        amplitudeLogEvent(amplitudeEvents.MENU)
        callYandexMetricGoal('clickMenu')
      }

      changeScrollPosition(menuFooter.value, window.scrollY)
      menuFooter.value = val
    }

    const openIframeModal = ({ title, src, contentClass = '', showCloseButton = false }) => {
      togglerModel.data = {
        type: 'iframe-modal',
        title,
        src,
        contentClass,
        showCloseButton
      }
      togglePageScroll(true)
    }

    const toggleIframeModal = () => {
      if (togglerModel.data && togglerModel.data.type === 'iframe-modal') {
        togglerModel.data = ''
        togglePageScroll(false)
        return
      }

      openIframeModal({
        title: 'Опрос',
        src: 'https://forms.yandex.ru/cloud/6684f8c6e010db9dad5ae676/?page=1'
      })
    }

    const openBonusIframeModal = () => {
      openIframeModal({
        title: 'Бонусная карта',
        src: 'https://form.p-h.app/cb7f40512dfef2674b676879a10142b1',
        contentClass: 'iframe-modal__content--bonus',
        showCloseButton: true
      })
    }

    const returnToPayOrder = () => {
      togglerModel.changeData('')
      callYandexMetricGoal('returntoorder')
    }

    const checkCashbox = async () => {
      try {
        const guestId = await sessionStorage.getItem('guest')

        const res = await orderFetch(
          // (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
          //   ? route.value.query.field
          //   : route.value.params.id,
          route.value.params.id,
          restaurantModel.code,
          metaModel.session,
          guestId,
          browserModel.browserId,
          route.value.query.order_guid
        )

        if (!res.active) {
          notificationModel.toggleNotification({
            status: true,
            text: 'Ошибка на кассе. <br /> Выберите ваш банк ещё раз',
            variant: 'danger'
          })
        }
        if (!res.tips) {
          res.tips = {}
        }

        // @ts-ignore
        res.tips.waiter = {
          ...res.waiter
        }
        return res
      } catch (e) {
        // eslint-disable-next-line
        console.log(e)
        notificationModel.toggleNotification({
          status: true,
          text: 'Ошибка на кассе. <br /> Выберите ваш банк ещё раз',
          variant: 'danger'
        })
        return false
      }
    }

    let translateRetryTimer = null
    let translateScriptPromise = null

    // Google translate
    const loadGoogleTranslateScript = () => {
      if (typeof window === 'undefined') {
        return Promise.resolve(false)
      }

      if (window.google?.translate?.TranslateElement) {
        return Promise.resolve(true)
      }

      if (translateScriptPromise) {
        return translateScriptPromise
      }

      translateScriptPromise = new Promise((resolve) => {
        window.TranslateInit = () => resolve(true)

        const existingScript = document.querySelector('script[data-google-translate]')
        if (existingScript) {
          resolve(false)
          return
        }

        const script = document.createElement('script')
        script.src = 'https://translate.google.com/translate_a/element.js?cb=TranslateInit'
        script.async = true
        script.dataset.googleTranslate = 'true'
        script.onerror = () => resolve(false)
        document.head.appendChild(script)
      })

      return translateScriptPromise
    }

    const googleTranslate = async () => {
      if (typeof window === 'undefined') {
        return
      }

      const runTranslate = () => {
        const googleTranslateElement = window?.google?.translate?.TranslateElement
        if (typeof googleTranslateElement !== 'function') {
          return false
        }

        try {
          googleTranslateElement({
            pageLanguage: 'ru'
          })
          return true
        } catch (e) {
          return false
        }
      }

      if (runTranslate()) {
        return
      }

      await loadGoogleTranslateScript()

      if (translateRetryTimer) {
        return
      }

      let retries = 0
      const maxRetries = 10
      translateRetryTimer = setInterval(() => {
        retries++
        if (runTranslate() || retries >= maxRetries) {
          clearInterval(translateRetryTimer)
          translateRetryTimer = null
        }
      }, 300)
    }

    // Яндекс цели
    const callYandexMetricGoal = (name) => {
      $yandexMetrika.reachGoal(name)
      // ym(85537528, 'reachGoal', name)
    }

    // Переключаем окно чаевых временное
    const togglePrecheck = () => {
      if (restaurantModel.stubTypes.includes('FULL')) {
        menuFooter.value = 'order'
      }
      precheck.value = !precheck.value
      togglePageScroll(precheck.value)
    }

    // Смена модалки общей
    const toggleModal = (val) => {
      modal.value = val
    }

    // Ожидание оплаты
    const paymentWaitMethods = async (val) => {
      togglePageScroll()
      await selectElements()
      if (val && val === 'changeBank') {
        pay()
      }
      if (val && val === 'changePaymentMethod') {
        togglePaymentMethodModal()
      }
      closePaymentWait()
    }

    // Снять выделенные элементы
    const selectElements = async () => {
      preloaderModel.status = true
      stopCheckPayment.value = true
      if (!orderModel.divideCheck || (orderModel.divideCheck && !diffCheckModel.status)) {
        if (
          orderModel.orderElements.length === 1 &&
          !orderModel.orderElements[0].selected &&
          orderModel.orderElements[0].selected_guest_id === null
        ) {
          const elements = orderModel.orderElements.map(i => i.id)
          await orderModel.selectOrderPosition({
            id: elements,
            status: true,
            guest: metaModel.guest,
            transactionReset: true
          })
        } else {
          const elements = orderModel.orderElements.filter(i => i.selected || i.selected_guest_id === metaModel.guest).map(i => i.id)
          await orderModel.selectOrderPosition({
            id: elements,
            status: true,
            guest: metaModel.guest,
            transactionReset: true
          })
        }
      }
      if (b2pError.value) {
        sbpModal.value = true
        b2pError.value = false
      }
      setTimeout(() => {
        stopCheckPayment.value = false
        preloaderModel.status = false
        return true
      }, 1000)
    }

    // FingerPrint
    // Не успел или упал — без записи fingerprint, чтобы не держать загрузку заказа
    const getVisitorData = async () => {
      const browserIds = [
        { browserId: $tapperBrowserPrint, plugin: 'tapperBrowserPrint' }
      ]
      let timer = null
      try {
        const fPrint = await Promise.race([
          $fingerprint.then(fp => fp.get()).then(({ visitorId }) => visitorId),
          new Promise((resolve) => {
            timer = setTimeout(() => resolve(null), FINGERPRINT_TIMEOUT_MS)
          })
        ])
        if (fPrint) {
          browserIds.unshift({ browserId: fPrint, plugin: 'fingerprint' })
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('fingerprint', e)
      } finally {
        clearTimeout(timer)
      }
      return browserIds
    }

    // показать/скрыть прелоадер
    const togglePreloader = () => {
      preloaderModel.status = !preloaderModel.status
    }

    // Закрытие окна ожидания оплаты
    const closePaymentWait = () => {
      sbpPayFlag.value = false
      paymentWait.value = false
      clearSbpWait()
    }

    const showSbpPaymentWait = () => {
      preloaderModel.status = false
      pageLoaded.value = true
      paymentMethod.value = 'sbp'
      sbpPayFlag.value = true
      paymentWait.value = true
    }

    const restoreSbpPaymentWait = () => {
      if (operationsModel.status) {
        clearSbpWait()
        return
      }

      const session = readSbpWait()
      if (!session) {
        return
      }

      operationsModel.referenceId = session.transactionId
      showSbpPaymentWait()
      callYandexMetricGoal('sbp_wait_restored')
      getPaymentStatus()
    }

    // Возврат из банка: тот же браузер (Back / focus) или reload с sessionStorage
    const returnFromSbp = () => {
      if (operationsModel.status) {
        clearSbpWait()
        return
      }
      if (!sbpPayFlag.value) {
        restoreSbpPaymentWait()
        return
      }
      if (paymentMethod.value === 'sbp' && sbpPayFlag.value) {
        showSbpPaymentWait()
        getPaymentStatus()
      }
    }

    // переключение между вариантами модалки банков
    const toggleSbpModal = (val) => {
      banksModal.value = val
    }

    // sbp модалка
    const closeSbpModal = () => {
      sbpModal.value = !sbpModal.value
      modalPayAlert.value = !modalPayAlert.value
    }

    // applyDiscount модалка
    const closeApplyDiscount = () => {
      applyDiscount.value = false
    }

    // Оплата
    const pay = () => {
      if (paymentMethod.value === 'sbp') {
        preloaderModel.status = false
        togglePageScroll(true)
        sbpModal.value = true
      } else {
        payCard()
      }
    }

    // Установка типа оплаты
    const setPaymentMethod = (val) => {
      paymentMethod.value = val
    }

    // Модалка закрытия оплаты
    const closeModalPayAlert = (val) => {
      if (val) {
        modalPayAlert.value = !modalPayAlert.value
        banksModal.value = 'priority'
        togglePageScroll()
      } else {
        modalPayAlert.value = !modalPayAlert.value
        sbpModal.value = !sbpModal.value
      }
    }

    // Модалка выбора типа оплаты
    const togglePaymentMethodModal = () => {
      preloaderModel.status = false
      if (!paymentMethodModal.value) {
        togglePageScroll(true)
      } else {
        togglePageScroll()
      }
      paymentMethodModal.value = !paymentMethodModal.value
    }

    // Оплата прошла
    const closeOperation = (status) => {
      if (orderModel.transactionStatus) {
        operationsModel.status = false
        if (status) {
          reviewModel.reviewIsShow = true
        }
        if (!status) {
          togglePageScroll(true)
          paymentMethodModal.value = true
        }
      } else {
        operationsModel.status = false
      }
    }

    // Закрытие отзывов
    const closeReview = async () => {
      await orderModel.fetchOrder({
        // tableId: (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
        //   ? route.value.query.field
        //   : route.value.params.id,
        tableId: route.value.params.id,
        domen: restaurantModel.code,
        session: metaModel.session,
        guest: metaModel.guest,
        browserId: fingerprintId.value,
        orderGuid: route.value.query.order_guid
      })
      setDiscount()
      // ----- Проверяем скидку по промокоду ----------------
      if (customLogicModel.list.promocode) {
        const savingOrderId = await $cookies.get('order_id')

        if (orderModel.orderData?.orders.length && savingOrderId === orderModel.orderData?.orders[0]?.order_id) {
          if (orderModel.orderData.discountStatus === 'WAITING') {
            waitingAcceptPromocode.value = true
          }

          if (orderModel.orderData.discountStatus === 'APPLIED') {
            acceptingPromocode.value = true
          }
        } else {
          acceptingPromocode.value = false
        }
      }
      // ----------------------------------------------------
      reviewModel.reviewIsShow = false
      if (!precheck.value) {
        togglePageScroll()
      }
    }

    // Разделить счет
    const toggleDiffCheck = () => {
      if (diffCheckModel.status) {
        orderModel.orderElements.forEach((element) => {
          if (element.selected) {
            orderModel.selectOrderPosition({
              id: element.id,
              status: true,
              guest: metaModel.guest
            })
          }
        })
      } else if (tipsType.value !== 'TAPPER') {
        amplitudeLogEvent(amplitudeEvents.SEPARATE_BILL)
        // percentsTips.value = 0
      } else {
        amplitudeLogEvent(amplitudeEvents.SEPARATE_BILL)
        // percentsTips.value = 10
      }

      $cookies.set('diffCheck', (!diffCheckModel.status).toString(), {
        maxAge: 60 * 60 * 24 * 1
      })
      diffCheckModel.status = !diffCheckModel.status
      onScrollTips()
    }

    // Включить/Выключить сервисный расход в оплату
    const toggleActiveServiceFee = () => {
      if (serviceFeeActive.value) {
        callYandexMetricGoal('transactions')
      }
      serviceFeeActive.value = !serviceFeeActive.value
    }

    // Оплата B2P
    const payCard = async (sbp, payWithoutDiscount) => {
      // Запускаем прелоадер
      preloaderModel.status = true

      const newOrderData = await checkCashbox()

      // Проверка на недоступность кассы
      if (!newOrderData || !newOrderData.active) {
        preloaderModel.status = false
        return
      }

      // Снимаем выделение позиций, если не раздельный счет и есть выделенные частично позиции
      if (!orderModel.divideCheck || (orderModel.divideCheck && !diffCheckModel.status)) {
        const elements = orderModel.orderElements.filter(i => i.selected || i.selected_guest_id === metaModel.guest).map(i => i.id)
        if (elements.length) {
          await orderModel.selectOrderPosition({
            id: elements,
            status: true,
            guest: metaModel.guest,
            transactionReset: true
          })
        }
      }

      // Возвращаем начальные значения для СБП
      sbpModal.value = ''
      banksModal.value = 'priority'

      // Проверка заглушки оплаты
      const lockPayStatus = await payModel.getStatus({
        restaurant: restaurantModel.code,
        // tableId: (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
        //   ? route.value.query.field
        //   : route.value.params.id
        tableId: route.value.params.id
      })
      if (lockPayStatus) {
        preloaderModel.vstatus = false
        return
      }

      // Находим все ID отмеченных позиций
      const positionId = getSelectedID(
        orderModel.orderElements,
        diffCheckModel.status
      )

      const diffCheckStatus = diffCheckModel.status

      const payedStatus = getStatusPayed(orderModel.orderElements, positionId)
      $cookies.set('order',
        JSON.stringify({
          payedStatus
        })
      )

      const res = checkChangeOrder({
        // tableId: (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
        //   ? route.value.query.field
        //   : route.value.params.id,
        tableId: route.value.params.id,
        domen: restaurantModel.code,
        newOrderData,
        guestId: metaModel.guest,
        oldArrayElements: orderModel.orderElements,
        oldDiscount: discount.value,
        waiter: orderModel.orderData.waiter,
        payedStatus,
        prepayment: orderModel.orderData.prepayment
      })

      // Проверяем, ожидается ли применение скидки
      if (!payWithoutDiscount && (orderModel.orderData.discountStatus === 'WAITING' || waitingAcceptPromocode.value)) { // Если нет флага оплаты без скидки и в новом orderData discountStatus === 'WAITING' или ждём применения промокода
        choosenBank.value = sbp ?? null
        onPaymentWithoutDiscount() // показываем модалку 'В заказе ещё не появилась скидка'
        return
      }

      if (res) {
        notificationModel.toggleNotification({
          status: true,
          text: res
        })

        const oldElementsId = orderModel.orderElements.filter(i => i.selected).map(i => i.id)
        const oldDiscount = orderModel.orderData.discount

        orderModel.updateOrder(newOrderData)

        if (diffCheckStatus && orderModel.divideCheck) {
          for (const i of oldElementsId) {
            await orderModel.selectOrderPosition({
              id: i,
              status: false,
              guest: metaModel.guest
            })
          }
        }

        // обновляем данные скидки
        setDiscount()

        if (discount.value === 0 && discount.value !== oldDiscount) {
          if (customLogicModel.list.promocode && acceptingPromocode.value) {
            waitingPromocode.value = false
            waitingAcceptPromocode.value = false
            acceptingPromocode.value = false
          }
        }

        if (discount.value !== 0 && discount.value !== oldDiscount) {
          if (customLogicModel.list.promocode && orderModel.orderData.discountStatus === 'APPLIED') {
            waitingPromocode.value = false
            waitingAcceptPromocode.value = false
            acceptingPromocode.value = true
          }
        }

        // Если сменился офик, перезаписываем его данные
        tipsModel.changeTipsPersonal({
          data: orderModel.orderData,
          sumOrders: totalSumWithPrepayment.value,
          increasedPercentage: customLogicModel.list.percentRest,
          increasedPercentageZero: customLogicModel.list.percentRestZero,
          increasedPercentageFive: customLogicModel.list.percentRestFive,
          increasedPercentageTwenty: customLogicModel.list.percentRestTwenty,
          newLogic: !customLogicModel.list.withoutNewLogicTips
        })
        onScrollTips()
        preloaderModel.status = false
        return
      }

      let url = window.location.href

      if (restaurantModel.advanceCheckType === 'ORDER_AND_TIPS') {
        url = parsUrlForPay(url)
      }

      const data = {
        url,
        restaurant_code: restaurantModel.code,
        order_id: orderModel.orderData.orders[0].order_id,
        position_id: positionId,
        amount: totalSum.value * 100 + (showTips.value ? tipsModel.tipsSum : 0) * 100,
        extra_service_amount: totalSum.value * 100,
        summTransaction: serviceFeeActive.value
          ? serviceFeeCalculate.value * 100
          : 0,
        safePayFlag: 'Y',
        tableCode: orderModel.orderData?.tableCode,
        waiter: orderModel.orderData.waiter && orderModel.orderData.waiter.user
          ? orderModel.orderData.waiter.user.id
          : null,
        guestId: metaModel.guest,
        tips: [
          {
            role: 'waiter',
            tips_amount: showTips.value ? tipsModel.tips.waiter.sum * 100 : 0
          },
          {
            role: 'hookah',
            tips_amount: tipsModel.tips.hookah.sum * 100
          },
          {
            role: 'kitchener',
            tips_amount: tipsModel.tips.kitchener.sum * 100
          },
          {
            role: 'sommelier',
            tips_amount: tipsModel.tips.sommelier.sum * 100
          },
          {
            role: 'barman',
            tips_amount: tipsModel.tips.barman.sum * 100
          },
          {
            role: 'barista',
            tips_amount: tipsModel.tips.barista.sum * 100
          },
          {
            role: 'team',
            tips_amount: tipsModel.tips.team.sum * 100
          }
        ]
      }

      if (sbp) {
        callYandexMetricGoal('selectBank')
        sbpPayFlag.value = true
        statusPayment.value = null
        paymentMethodModal.value = false
        data.payment_type = 'SBP_B'
        data.schema_prefix = sbp.schemaPrefix
        if (sbp.email.length) {
          data.email = sbp.email
        }
      }

      let b2pRespone = false
      try {
        b2pRespone = await orderPay(data)
      } catch (e) {
        notificationModel.toggleNotification({
          status: true,
          text: 'Ошибка на кассе. <br /> Выберите ваш банк ещё раз',
          variant: 'danger'
        })
        b2pRespone = false
        b2pError.value = true
      }

      if (b2pRespone) {
        operationsModel.referenceId = b2pRespone.transaction_id
      } else {
        closePaymentWait()
        await selectElements()
        return
      }

      if (qrType.value === 'precheck' || qrType.value === 'precheck2') {
        await sendStatistic(metaModel.session, 'precheck')
      }

      if (sbp) {
        saveSbpWait({
          transactionId: String(b2pRespone.transaction_id),
          schemaPrefix: sbp.schemaPrefix
        })
      }

      if (sbp && b2pRespone.pay_web_url) {
        callYandexMetricGoal('sbp_redirect_web')
        // Wait и выключенный прелоадер нужны до assign: Back из банка
        // часто поднимает страницу из bfcache в том состоянии, в каком ушли.
        showSbpPaymentWait()
        window.location.assign(b2pRespone.pay_web_url)
        return
      }

      if (sbp) {
        callYandexMetricGoal('sbp_redirect_deeplink')
      }

      window.location.href = b2pRespone.link
      // Имитация клика по ссылке для Safari (только deeplink)
      const sbpLink = b2pRespone.link
      const hiddenLink = document.getElementById('hiddenSbpLink')
      hiddenLink.href = sbpLink
      hiddenLink.click() // Safari воспринимает это как действие пользователя

      if (sbpPayFlag.value) {
        paymentWait.value = true
      }
      setTimeout(() => {
        if (diffCheckStatus) {
          diffCheckModel.status = true
        }
        preloaderModel.status = false
      }, 3000)
    }

    // Проверка на пречек
    const checkPrecheck = () => {
      if (restaurantModel.advanceCheckType === 'ORDER_AND_TIPS') {
        noFooter.value = true
        precheck.value = true
        togglePageScroll(true)
      }
    }

    const refreshPage = async () => {
      const session = await $cookies.get('session')
      await orderModel.fetchOrder({
        // tableId: (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
        //   ? route.value.query.field
        //   : route.value.params.id,
        tableId: route.value.params.id,
        domen: restaurantModel.code,
        session: null,
        guest: metaModel.guest,
        browserId: fingerprintId.value,
        orderGuid: route.value.query.order_guid
      })
      // Получаем сессию
      metaModel.session = orderModel.orderData.session
      !session &&
        $cookies.set('session', String(orderModel.orderData.session))

      runSockets()

      // Загружаем данные для проекта
      tipsType.value = orderModel.orderData.tipsType
      setDiscount()
      recipient.value = orderModel.orderData.recipient
      responseTemplate.value = orderModel.orderData.responseTemplate
      currencyTypeSelected.value = orderModel.orderData.currencyTypeSelected

      setDiscount()
      const loyaltyCode = orderModel.orderData?.global_system_loyalty?.code
      if (loyaltyCode === 'prime_hill') {
        await fetchPrimeHillUser()
      } else if (loyaltyCode === 'iiko_card') {
        await fetchIikoCardUser()
      }

      if (!orderModel.orderData.session) {
        notificationModel.toggleNotification({
          status: true,
          text: 'Обновлено, но заказ ещё не создан'
        })
      }

      onScrollTips()
    }

    const cloudOnSuccess = (val) => {
      preloaderModel.status = true
      const link = window.location + `?operation&reference=${val.invoiceId}`
      window.location.href = link
    }

    const toggleApplyDiscount = () => {
      applyDiscount.value = !applyDiscount.value
    }

    const getPaymentStatus = () => {
      const stopInterval = () => {
        clearInterval(intervalResponse)
      }

      let counter = 0
      const intervalResponse = setInterval(async () => {
        counter++
        try {
          const responseStatus = await orderModel.checkTransaction(
            operationsModel.referenceId
          )

          if (!responseStatus) {
            return
          }

          statusPayment.value = responseStatus?.status
          if (!statusPayment.value) {
            messageErrorPayment.value = responseStatus?.message
          }
          if (statusPayment.value && sbpPayFlag.value) {
            statusPayment.value = null
            sbpPayFlag.value = false
            operationsModel.status = true
            clearSbpWait()
            setTimeout(() => {
              statusPayment.value = true
            }, 3000)
          }

          stopInterval()
        } catch (error) {
          // eslint-disable-next-line
          console.log('error', error)
          if (counter === 20 || stopCheckPayment.value) {
            statusPayment.value = false
            stopInterval()
          }
        }
      }, 1500)
    }

    const onScanner = () => {
      scanner.value = !scanner.value
    }

    const onWaitingDiscount = () => {
      waitingDiscount.value = true
    }

    const onNumberCardScanner = (val) => {
      numberCard.value = val
      scanner.value = false
      waitingDiscount.value = true
      cardNovikov.value()
    }

    const applicationOfDiscount = (data) => {
      const onCode = data.result.code
      if (onCode === 105) {
        waitingDiscount.value = false
        failureApplyDiscount.value = true
        setTimeout(() => {
          failureApplyDiscount.value = false
        }, 3000)
      } else if (onCode === 103) {
        waitingDiscount.value = false
        successfulScan.value = true
        setTimeout(() => {
          successfulScan.value = false
        }, 3000)
      } else {
        setTimeout(() => {
          if (discount.value === 0) {
            waitingDiscount.value = false
            scanLoading.value = true
            setTimeout(() => {
              scanLoading.value = false
            }, 3000)
          }
        }, 7000)
      }
    }

    const cardNovikov = async () => {
      try {
        const { data } = await discountGetData({
          sessions_id: orderModel.orderData.session,
          card_id: numberCard.value
        })
        applicationOfDiscount(data)
      } catch (e) {
        waitingDiscount.value = false
        failureApplyDiscount.value = true
        setTimeout(() => {
          failureApplyDiscount.value = false
        }, 3000)
      }
    }

    const onScrollTips = async () => {
      const scrollTipsCookies = await $cookies.get('scrollTipsCookies')
      const elementOrderList = document.querySelector('#orderList')
      if (!scrollTipsCookies && themeModel.theme === 'NOVIKOV') {
        elementOrderList.style.height = elementOrderList.scrollHeight + 'px'
        const orderListHeight = elementOrderList.scrollHeight + 10

        const options = {
          container: '#mainPageContent',
          offset: -170
        }

        window.scrollTo('#tipsId', orderListHeight, options)
        $cookies.set('scrollTipsCookies', true)
      }
      if (elementOrderList) {
        elementOrderList.style.height = 'auto'
      }
    }

    const onDisabledWaitingDiscount = () => {
      disabledWaitingDiscount.value = true
    }

    const onDiscountProcess = () => {
      discountProcess.value = !discountProcess.value
    }

    const onPaymentWithoutDiscount = () => {
      paymentWithoutDiscount.value = !paymentWithoutDiscount.value
    }

    const toggleHasDiscount = () => {
      hasDiscount.value = !hasDiscount.value
    }

    const togglePromocodeError = () => {
      promocodeError.value = !promocodeError.value
    }

    const toggleWaitingPromocode = () => {
      waitingPromocode.value = !waitingPromocode.value
    }

    const toggleWaitingAcceptPromocode = () => {
      waitingAcceptPromocode.value = !waitingAcceptPromocode.value
    }

    const confirmPromocode = async () => {
      const guestId = await sessionStorage.getItem('guest')
      const oldElementsId = orderModel.orderElements.filter(i => i.selected).map(i => i.id)
      await orderModel.fetchOrder({
        // tableId: (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
        //   ? route.value.query.field
        //   : route.value.params.id,
        tableId: route.value.params.id,
        domen: restaurantModel.code,
        session: null,
        guest: guestId,
        browserId: fingerprintId.value,
        orderGuid: route.value.query.order_guid
      })
      if (diffCheckModel.status) {
        for (const i of oldElementsId) {
          await orderModel.selectOrderPosition({
            id: i,
            status: false,
            guest: metaModel.guest
          })
        }
      }

      setDiscount()

      togglePreloader()
      toggleWaitingAcceptPromocode()
      acceptingPromocode.value = true
      waitingPromocode.value = false
      successfulScan.value = true
      setTimeout(() => {
        successfulScan.value = false
      }, 3000)
    }

    const closeActiveModalBonus = (val) => {
      if (!val) {
        callYandexMetricGoal('PHRefusal')
      }
      activeModalBonus.value = ''
    }

    const changeActiveModalBonus = (val) => {
      activeModalBonus.value = val
    }

    const setDiscount = () => {
      discount.value =
        parseInt(orderModel.orderData.discount) !== 0
          ? parseInt(orderModel.orderData.discount) / 100
          : 0
    }

    // Получаем юзера Prime Hill или iikoCard
    const fetchPrimeHillUser = async () => {
      const isVerificationOn = value => ['ON', 'TRUE', '1', 'YES', 'Y'].includes(String(value ?? '').trim().toUpperCase())
      const orderData = orderModel.orderData

      if (!orderData) {
        return
      }

      if (orderData.global_system_loyalty?.code === 'prime_hill') {
        const primeHillWasGreeting = await Cookie.get('primeHillWasGreeting')
        const primeHillPhone = await Cookie.get('primeHillPhone')
        if (primeHillPhone && orderData.session) {
          const verifiedPhone = await Cookie.get('primeHillCodeVerifiedPhone')
          const isCodeVerifiedOnDevice = verifiedPhone && verifiedPhone.toString() === primeHillPhone.toString()
          const needCodeVerification = isVerificationOn(orderData.global_system_loyalty?.verification)
          if (needCodeVerification && !isCodeVerifiedOnDevice) {
            return
          }

          const payload = {
            tel: primeHillPhone.toString(),
            session: orderData.session
          }
          await loyaltyModel.fetchUserPrimeHill(payload)
          if (primeHillWasGreeting !== restaurantModel.code) {
            Cookie.set('primeHillWasGreeting', restaurantModel.code, {
              maxAge: 60 * 60 * 12
            })
            activePopUpGreeting.value = true
            setTimeout(() => {
              activePopUpGreeting.value = false
            }, 3000)
          }
        }
      } else if (orderData.global_system_loyalty?.code === 'iiko_card') {
        const primeHillWasGreeting = await Cookie.get('primeHillWasGreeting')
        const primeHillPhone = await Cookie.get('primeHillPhone')
        if (primeHillPhone && orderData.session) {
          const verifiedPhone = await Cookie.get('primeHillCodeVerifiedPhone')
          const isCodeVerifiedOnDevice = verifiedPhone && verifiedPhone.toString() === primeHillPhone.toString()
          const needCodeVerification = isVerificationOn(orderData.global_system_loyalty?.verification)
          if (needCodeVerification && !isCodeVerifiedOnDevice) {
            return
          }

          const payload = {
            tel: primeHillPhone.toString(),
            session: orderData.session
          }
          await loyaltyModel.fetchUserIikoCard(payload)
          if (primeHillWasGreeting !== restaurantModel.code) {
            Cookie.set('primeHillWasGreeting', restaurantModel.code, {
              maxAge: 60 * 60 * 12
            })
            activePopUpGreeting.value = true
            setTimeout(() => {
              activePopUpGreeting.value = false
            }, 3000)
          }
        }
      }
    }

    // Запомнить позицию вертикального скролла
    const changeScrollPosition = (section, position) => {
      verticalScroll[section] = position
    }

    // Сокеты
    // listen у Echo добавляет обработчик, а не заменяет: повторная подписка дублирует обработку событий
    let socketChannelName = null
    const leaveSocketChannel = () => {
      if (socketChannelName) {
        $echo?.leave(socketChannelName)
        socketChannelName = null
      }
    }

    const runSockets = () => {
      const channelName = `${process.env.PREFIX_SOCKET}session.${metaModel.session}`
      if (channelName === socketChannelName) {
        return
      }
      leaveSocketChannel()
      socketChannelName = channelName

      $echo
        .channel(channelName)
        .listen('ChangeOrderPosition', async (e) => {
          const guestId = await sessionStorage.getItem('guest')
          switch (e.message) {
            case 'change position':
              // eslint-disable-next-line
              console.log('SOCKET change position')
              if (Array.isArray(e.massive.positions_id)) {
                e.massive.positions_id.forEach((i) => {
                  const data = {
                    ...e.massive,
                    positions_id: i,
                    isArr: Array.isArray(e.massive.positions_id)
                  }
                  orderModel.checkOrderPosition(data)
                })
              } else {
                orderModel.checkOrderPosition(e.massive)
              }
              break
            case 'update order':
              // eslint-disable-next-line
                console.log('SOCKET update order')

              // eslint-disable-next-line no-case-declarations
              const oldElementsId = orderModel.orderElements.filter(i => i.selected).map(i => i.id)
              // eslint-disable-next-line no-case-declarations
              const oldDiscount = orderModel.orderData.discount

              // Получаем актуальный заказ
              await orderModel.fetchOrder({
                // tableId: (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
                //   ? route.value.query.field
                //   : route.value.params.id,
                tableId: route.value.params.id,
                domen: restaurantModel.code,
                session: null,
                guest: guestId,
                browserId: browserModel.browserId
              })

              if (diffCheckModel.status) {
                for (const i of oldElementsId) {
                  await orderModel.selectOrderPosition({
                    id: i,
                    status: false,
                    guest: metaModel.guest
                  })
                }
              }

              discount.value =
                parseInt(orderModel.orderData.discount) !== 0
                  ? parseInt(orderModel.orderData.discount) / 100
                  : 0

              if (discount.value !== 0 && discount.value !== oldDiscount) {
                if (customLogicModel.list.promocode && waitingAcceptPromocode.value) {
                  waitingPromocode.value = false
                  waitingAcceptPromocode.value = false
                  acceptingPromocode.value = true
                }
                waitingDiscount.value = false
                successfulScan.value = true
                setTimeout(() => {
                  successfulScan.value = false
                }, 3000)
              }
              break

            case 'waiting discount':
              // eslint-disable-next-line
              console.log('SOCKET waiting discount')
              break

            case 'pay position':
              // eslint-disable-next-line
              console.log('SOCKET pay position')
              paymentMethodModal.value = false
              sbpModal.value = false
              orderModel.payedOrderElement(e.massive)
              break
            case 'pay error':
              // eslint-disable-next-line
              console.log('ERROR !!!!!')
              // this.CHECK_TRANSACTION_FALSE()
              // eslint-disable-next-line
              console.log('pay error', e.massive)
              break
            default:
              break
          }
        })

        .listen('WaitingDiscount', (e) => {
          switch (e.message) {
            case 'waiting discount':
              // eslint-disable-next-line
              console.log('SOCKET waiting discount')
              disabledWaitingDiscount.value = true
          }
        })

        .listen('PartialPayment', (e) => {
          // eslint-disable-next-line
          console.log('SOCKET PartialPayment', e)
          orderModel.transactionStatus = true
        })
        .listen('CloseOrder', (e) => {
          // eslint-disable-next-line
          console.log('SOCKET CloseOrder')
          if (!e.massive || (e.massive && !e.massive.guest) || (e.massive && e.massive.guest !== metaModel.guest)) {
            orderModel.fetchOrder({
              // tableId: (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
              //   ? route.value.query.field
              //   : route.value.params.id,
              tableId: route.value.params.id,
              domen: restaurantModel.code,
              // Со старой сессией бэкенд снова шлёт CloseOrder в её канал — получается цикл перезапросов
              session: null,
              guest: metaModel.guest,
              browserId: fingerprintId.value,
              orderGuid: route.value.query.order_guid
            })
          }
        })

      // События между загрузкой заказа и входом в канал не приходят — догружаем заказ после подписки
      const socket = $echo?.connector?.socket
      if (socket?.connected) {
        resyncOrder(true)
      } else {
        socket?.off('connect', onSocketFirstConnect)
        socket?.once('connect', onSocketFirstConnect)
      }
    }

    const onSocketFirstConnect = () => {
      resyncOrder(true)
    }

    // Перезапрос заказа после возврата на вкладку и переподключения сокета:
    // пока вкладка в фоне или сокет отключён, события стола теряются
    const RESYNC_MIN_HIDDEN_MS = 3000
    const RESYNC_MIN_INTERVAL_MS = 5000
    const RESYNC_MAX_ATTEMPTS = 2
    const RESYNC_RETRY_DELAY_MS = 1500
    let resyncInProgress = false
    let resyncPending = false
    let lastResyncAt = 0
    let hiddenAt = 0

    // Не вмешиваемся в оплату и в незавершённый выбор позиции
    const canResyncOrder = () => {
      return !preloaderModel.status &&
        !operationsModel.status &&
        !paymentWait.value &&
        !sbpPayFlag.value &&
        !orderModel.orderElements.some(i => i.loading)
    }

    // force — без ограничения частоты (переподключение сокета: события за время обрыва потеряны)
    const resyncOrder = async (force = false) => {
      if (!metaModel.session) {
        return
      }
      if (resyncInProgress) {
        resyncPending = true
        return
      }
      if (
        (!force && Date.now() - lastResyncAt < RESYNC_MIN_INTERVAL_MS) ||
        !canResyncOrder()
      ) {
        return
      }
      resyncInProgress = true
      lastResyncAt = Date.now()
      try {
        for (let attempt = 0; attempt < RESYNC_MAX_ATTEMPTS; attempt++) {
          if (attempt > 0) {
            await new Promise(resolve => setTimeout(resolve, RESYNC_RETRY_DELAY_MS))
          }
          if (!canResyncOrder()) {
            break
          }
          const result = await orderModel.refreshOrder({
            tableId: route.value.params.id,
            domen: restaurantModel.code,
            session: null,
            guest: metaModel.guest,
            browserId: fingerprintId.value,
            orderGuid: route.value.query.order_guid
          }, canResyncOrder)
          if (result === 'applied') {
            setDiscount()
          }
          if (result !== 'changed') {
            break
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('resyncOrder', e)
      } finally {
        resyncInProgress = false
        if (resyncPending) {
          resyncPending = false
          resyncOrder(true)
        }
      }
    }

    const onSocketReconnect = () => {
      resyncOrder(true)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }
      if (hiddenAt && Date.now() - hiddenAt >= RESYNC_MIN_HIDDEN_MS) {
        resyncOrder()
      }
      hiddenAt = 0
    }

    onBeforeUnmount(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      leaveSocketChannel()
      $echo?.connector?.socket?.off('reconnect', onSocketReconnect)
      $echo?.connector?.socket?.off('connect', onSocketFirstConnect)
    })

    // <<<---------- Mounted ---------->>>
    onMounted(async () => {
      // Фиксируем дату старта загрузки приложения
      const startAppDate = new Date()
      const isPaymentReturn = operationsModel.status

      try {
        // Сброс формы отзыва при загрузке страницы
        reviewSubmitted.value = false
        rating.value = 0
        previousRating.value = 0
        comment.value = ''

        // проверка ID реста на null
        // restaurantModel.chekRestId((route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
        //   ? route.value.query.field
        //   : route.value.params.id, route.value.fullPath, redirect)
        restaurantModel.chekRestId(route.value.params.id, route.value.fullPath, redirect)

        // Сервер не дождался кода ресторана (precheck2) — определяем на клиенте
        if (!restaurantModel.code) {
          await retryOnNetworkError(() => restaurantModel.getCode({
            ...getRestCodePayload(route.value),
            timeout: SCREEN_REQUEST_TIMEOUT_MS
          }))
          applyAdvanceCheckType(route.value, url => window.location.replace(url))
          if (restaurantModel.advanceCheckType === 'TIPS') {
            return
          }
        }

        // Не зависят от статуса ресторана — запускаем сразу. ID ресторана нужен только меню, заказ его не ждёт
        const restIdPromise = restaurantModel.getIdRestByCode(restaurantModel.code, SCREEN_REQUEST_TIMEOUT_MS)
        restIdPromise.catch(() => {})
        visitorDataPromise = getVisitorData().then((browserId) => {
          browserModel.browserId = browserId
        })

        // Заглушка, если ресторан не работает
        await restaurantModel.getStatus({
          restaurant: restaurantModel.code,
          // tableId: (route.value.query.qr_type !== 'precheck' && route.value.query.qr_type !== 'precheck2')
          //   ? route.value.query.field
          //   : route.value.params.id
          tableId: route.value.params.id,
          timeout: SCREEN_REQUEST_TIMEOUT_MS
        })

        if (restaurantModel.advanceCheckType === 'ORDER_AND_TIPS' && restaurantModel.stubTypes.includes('FULL')) {
          precheck.value = true
        }

        // Получаем список банков для СБП
        bankModel.fetchAllBanks()

        // Нестандартная логика для определенных рестов
        await customLogicModel.runCustomLogic(restaurantModel.code)

        // Amplitude
        amplitudeInitialize()

        // Получение темы ресторана
        themeModel.fetch(restaurantModel.code)

        // Stock
        stocksModel.fetch(restaurantModel.code).catch((e) => {
          // eslint-disable-next-line no-console
          console.log('Stocks loading failed', e)
        })

        // При возврате из оплаты СБП (тот же браузер: focus / Back из bfcache)
        window.addEventListener('focus', () => {
          returnFromSbp()
        })
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) {
            returnFromSbp()
          }
        })
        restoreSbpPaymentWait()

        document.addEventListener('visibilitychange', onVisibilityChange)
        $echo?.connector?.socket?.on('reconnect', onSocketReconnect)

        // Очищаем заказ
        orderModel.clearOrderData()

        const paymentMethodFromCookie = await $cookies.get('paymentMethod')
        if (paymentMethodFromCookie) {
          paymentMethod.value = paymentMethodFromCookie
        }

        // Если у ресторана не работает СБП, по умолчанию метод оплаты картой
        if (restaurantModel.stubTypes.includes('SBP') || restaurantModel.stubTypes.includes('WITHOUT_TIPS')) {
          paymentMethod.value = 'card'
        }

        // Нет заказа, нет офика, нет семьи
        if (restaurantModel.advanceCheckTypee === 'ORDER_AND_TIPS') {
          redirectEmptyOrderForTips(orderModel.orderData, !showOrderList.value.active, tipsModel.tipsUrl)
        }

        // Получаем меню параллельно с заказом: для menu-QR столов ждём ответ, для остальных — в фоне
        const tableIdForMenuRules = restaurantModel.tableCode || route.value.params.id
        const menuPromise = restIdPromise
          .then(() => menuModel.fetchMenu({ restId: restaurantModel.restId, restName: restaurantModel.code }))
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.log('Menu loading failed', e)
          })

        initReachedOrder = true
        if (!restaurantModel.stubTypes.includes('FULL')) {
          await getOrderData()
        } else {
          callYandexMetricGoal('cashboxNotWork')
        }

        if (isMenuQrTable(restaurantModel.code, tableIdForMenuRules)) {
          await menuPromise
        }
        // ----

        // fix for 100vh IOS
        togglePageScroll()

        // turn off modal
        setTimeout(() => {
          warningModel.toggleStatus(
            {
              text: '',
              status: false
            },
            4000
          )
        })

        onScrollTips()

        // Проверка на VPN
        checkVpn(notificationModel.toggleNotification)

        // Определяем активную вкладку (Заказ или меню)
        const tableId = restaurantModel.tableCode || route.value.params.id
        const forceMenuTab = isMenuQrTable(restaurantModel.code, tableId)

        if (forceMenuTab) {
          menuFooter.value = 'menu'
          callYandexMetricGoal('teleportToMenu')
          if (!orderModel.orderElements.length) {
            callYandexMetricGoal('emptyOrder')
          } else {
            callYandexMetricGoal('fullOrder')
          }
        } else if (!orderModel.orderElements.length) {
          callYandexMetricGoal('emptyOrder')

          if (menuModel.menuUser.length && restaurantModel.code !== 'china-club-9122' && !customLogicModel.list.noMenu && (!customLogicModel.list.noFooter || !noFooter.value)) {
            menuFooter.value = 'menu'
            callYandexMetricGoal('teleportToMenu')
          } else {
            menuFooter.value = 'order'
          }
        } else {
          menuFooter.value = 'order'
          callYandexMetricGoal('fullOrder')
        }

        // Восстановление раздельного счета
        await restoreDiffCheck()

        // если чаевые 0, то переключаем оплату на СПБ
        // if (tipsModel.tipsSum === 0) {
        //   paymentMethod.value = 'sbp'
        // }
      } catch (e) {
        // Несуществующий ресторан: shop/availability отвечает 404
        if (!initReachedOrder && e?.response?.status === 404) {
          router.replace('/404')
          return
        }
        reportOrderLoadError(e)
        orderLoadError.value = !initReachedOrder || (!restaurantModel.stubTypes.includes('FULL') && !orderModel.orderData)
        if (!menuFooter.value) {
          menuFooter.value = 'order'
        }
      }

      // Страница загрузилась
      // фиксируем время загрузки страницы
      const endAppDate = new Date()
      const loadAppDate = endAppDate - startAppDate
      // Только после загрузки заказа: checkTransaction отмечает оплаченные позиции и transactionStatus,
      // а clearOrderData и fetchOrder их перезаписывают
      if (operationsModel.status && operationsModel.referenceId) {
        getPaymentStatus()
      }
      // Гость, вернувшийся с оплаты, ждёт результат платежа — анимацию не затягиваем
      const preloaderMinMs = isPaymentReturn ? 0 : PRELOADER_MIN_MS
      if (loadAppDate < preloaderMinMs) {
        setTimeout(() => {
        // Скрываем прелоадер
          pageLoaded.value = true
          amplitudeLogEvent(amplitudeEvents.SCAN_QR)
          amplitudeLogEvent(amplitudeEvents.REST_NAME, { Ресторан: restaurantModel.code })
          // Если касса не доступна показываем модалку
          if (restaurantModel.stubTypes.includes('FULL') && !restaurantModel.advanceCheckType === 'ORDER_AND_TIPS') {
            restaurantModel.showModal = true
          }
          primeHillReminder()
        }, preloaderMinMs - loadAppDate)
      } else {
        pageLoaded.value = true
        amplitudeLogEvent(amplitudeEvents.SCAN_QR)
        amplitudeLogEvent(amplitudeEvents.REST_NAME, { Ресторан: restaurantModel.code })
        primeHillReminder()
      }
    })

    return {
      // AsyncData
      asyncData,
      // Data
      pageLoaded,
      orderLoadError,
      orderLoadRetrying,
      discount,
      recipient,
      failureApplyDiscount,
      responseTemplate,
      serviceFeeActive,
      currencyTypeSelected,
      modalPolicy,
      tipsType,
      statusPayment,
      messageErrorPayment,
      paymentMethod,
      paymentMethodModal,
      sbpModal,
      modalPayAlert,
      banksModal,
      sbpPayFlag,
      paymentWait,
      applyDiscount,
      waitingDiscount,
      discountProcess,
      disabledWaitingDiscount,
      scanLoading,
      successfulScan,
      scanner,
      paymentWithoutDiscount,
      menuFooter,
      hasDiscount,
      promocodeError,
      waitingPromocode,
      acceptingPromocode,
      waitingAcceptPromocode,
      activeModalBonus,
      activePopUpGreeting,
      verticalScroll,
      choosenBank,
      stopCheckPayment,
      modal,
      b2pError,
      qrType,
      noFooter,
      hideFooterForMenuQr,
      precheck,
      rating,
      previousRating,
      comment,
      disabledReviewBtn,
      reviewSubmitted,

      // Computed
      fingerprintId,
      selectedElements,
      computedTipsType,
      calcNotPayedElements,
      currencyIcon,
      currencyType,
      serviceFeeCalculate,
      showOrderList,
      separateOrderAlienGuest,
      selectedSum,
      selectedSumWithPrepeyment,
      totalSum,
      totalSumWithPrepayment,
      totalSumToPay,
      showBlockTips,
      showTips,
      serviceFeePercent,

      // Methods
      getOrderData,
      retryOrderLoad,
      checkOrderData,
      callYandexMetricGoal,
      togglePrecheck,
      toggleModal,
      paymentWaitMethods,
      selectElements,
      getVisitorData,
      togglePreloader,
      closePaymentWait,
      returnFromSbp,
      toggleSbpModal,
      closeSbpModal,
      closeApplyDiscount,
      pay,
      setPaymentMethod,
      closeModalPayAlert,
      togglePaymentMethodModal,
      closeOperation,
      closeReview,
      toggleDiffCheck,
      toggleActiveServiceFee,
      payCard,
      refreshPage,
      callWaiter,
      cloudOnSuccess,
      toggleApplyDiscount,
      getPaymentStatus,
      onScanner,
      onWaitingDiscount,
      onNumberCardScanner,
      applicationOfDiscount,
      cardNovikov,
      onScrollTips,
      onDisabledWaitingDiscount,
      onDiscountProcess,
      onPaymentWithoutDiscount,
      toggleHasDiscount,
      togglePromocodeError,
      toggleWaitingPromocode,
      toggleWaitingAcceptPromocode,
      confirmPromocode,
      closeActiveModalBonus,
      changeActiveModalBonus,
      setDiscount,
      fetchPrimeHillUser,
      changeScrollPosition,
      returnToPayOrder,
      googleTranslate,
      toggleIframeModal,
      openBonusIframeModal,
      toggleMenuFooter,
      checkPrecheck,
      handleRating,
      onChangeComment,
      sendReview,

      // store
      customLogicModel,
      restaurantModel,
      warningModel,
      operationsModel,
      togglerModel,
      wifiModel,
      themeModel,
      preloaderModel,
      notificationModel,
      orderModel,
      orderModelWidget,
      privacyPolicyModel,
      menuModel,
      stocksModel,
      diffCheckModel,
      payModel,
      browserModel,
      callWaiterModel,
      tipsModel,
      loyaltyModel,
      reviewModel
    }
  },

  head () {
    return {
      title: this.title,
      meta: [
        { name: 'og:title', content: 'Tapper' },
        { name: 'og:description', content: this.asyncData?.descMeta }
      ],
      link: this.restaurantModel.code
        ? [{ rel: 'preload', as: 'image', href: `${process.env.IMG_URL}/loader/${this.restaurantModel.code}.gif`, fetchpriority: 'high' }]
        : []
    }
  }
})
